/**
 * Bi-directional sticky for the product info column.
 *
 * The info card is taller than the viewport, so a single native CSS sticky
 * (top OR bottom) can't keep both its top and bottom reachable while reading
 * the (even taller) left media column. This drives one native `position: sticky`
 * element with a dynamically-clamped `top` value:
 *
 *   top is clamped to  [ bottomStick , topPad ]
 *     topPad      = header height + gap   → card pinned just below the header
 *     bottomStick = viewportH - pad - cardH → card bottom pinned near viewport bottom
 *
 * On each scroll frame we shift `top` by the inverse of the scroll delta and
 * clamp it to that range:
 *
 *   • Between the bounds the stick point moves with the content at the same rate,
 *     so the card scrolls naturally (native sticky stays in tracking mode — no
 *     per-frame relative repositioning, so no jitter against smooth scroll).
 *   • At a bound the browser's own sticky implementation pins the card
 *     (compositor-driven → smooth) at the top or bottom edge.
 *
 * Scroll down → top drifts toward bottomStick → card eventually bottom-sticks.
 * Scroll up   → top drifts toward topPad      → card eventually top-sticks,
 *               landing EXACTLY at topPad (never passes through / under the menu).
 */
(function () {
  'use strict';

  var BOTTOM_PAD = 24;
  var HEADER_GAP = 16;
  var DESKTOP_BP = 750;

  function init() {
    var stickyEl = document.querySelector('.product__info-container');
    var wrapper  = document.querySelector('.product__info-wrapper');
    var scrollEl = document.querySelector('.product__media-wrapper');
    if (!stickyEl || !wrapper || !scrollEl) return;

    var headerEl = document.querySelector('sticky-header');

    var enabled     = false;
    var currentTop  = null; // current sticky `top` value (px)
    var prevMaxTop  = null; // topPad from the previous frame (header-growth detection)
    var lastScrollY = window.scrollY;

    function getHeaderBottom() {
      // The lowest VIEWPORT y that the sticky header stack covers — i.e. the line
      // the card must sit below. We read live getBoundingClientRect().bottom (not
      // offsetHeight) so this also handles the header's hidden/revealing transform
      // states (bottom goes <= 0 when the header is translated off-screen).
      //
      // Why offsetHeight was wrong: the custom/Globo mega-menu row lives inside
      // .header-secondary__wrapper, which collapses to height:0 when scrolled — but
      // with overflow:visible, so the menu OVERFLOWS downward (~104px) below the
      // 70px main bar. sticky-header.offsetHeight reported only 70, so the card
      // pinned at 86 while the menu painted down to ~173 → the menu overlapped it.
      var bottom = 0;

      if (headerEl) {
        bottom = headerEl.getBoundingClientRect().bottom;
      }

      // Account for the overflowing mega-menu row. When scrolled-past-header sets
      // overflow:hidden on the wrapper the menu is clipped away, so fall back to the
      // wrapper's own (clipped) bottom instead of the menu's overflowing box.
      var wrap = document.querySelector('.header-secondary__wrapper');
      var mega = document.querySelector('#header-secondary__mega-menu');
      if (wrap && mega) {
        var wcs = window.getComputedStyle(wrap);
        var clipped = wcs.overflow === 'hidden' || wcs.overflowY === 'hidden';
        var ref = clipped ? wrap.getBoundingClientRect().bottom
                          : mega.getBoundingClientRect().bottom;
        if (ref > bottom) bottom = ref;
      }

      return bottom > 0 ? bottom : 0;
    }

    function getTopPad() {
      return getHeaderBottom() + HEADER_GAP;
    }

    function getBottomStick() {
      return window.innerHeight - BOTTOM_PAD - stickyEl.offsetHeight;
    }

    function clamp(v, lo, hi) {
      return v < lo ? lo : (v > hi ? hi : v);
    }

    function ensureMinHeight() {
      var stickyH   = stickyEl.offsetHeight;
      var viewportH = window.innerHeight;
      var topPad    = getTopPad();
      var scrollH   = scrollEl.offsetHeight;

      // The wrapper needs exactly enough height for the card to travel from
      // top-stick to bottom-stick. The travel distance is the amount the card
      // exceeds the visible area between header and viewport bottom.
      var available = viewportH - topPad - BOTTOM_PAD;
      var overflow  = stickyH - available;
      var needed    = stickyH + overflow; // = 2*stickyH - available

      // Never shrink below the left column height.
      wrapper.style.minHeight = (scrollH > needed ? scrollH : needed) + 'px';
    }

    function disable() {
      enabled = false;
      stickyEl.style.position = '';
      stickyEl.style.top = '';
      wrapper.style.minHeight = '';
    }

    // Recompute and write `top`. deltaScroll is the page scroll since last frame
    // (0 when called for a header/viewport resize rather than a scroll).
    function applyTop(deltaScroll) {
      var maxTop = getTopPad();
      var minTop = getBottomStick();

      if (minTop >= maxTop) {
        // Card fits between header and viewport bottom — plain top sticky.
        currentTop = maxTop;
      } else if (deltaScroll === 0) {
        // Header height or viewport changed without a scroll (e.g. the Globo
        // secondary nav finishing its expand transition after scrolling stops).
        // If the card is currently top-stuck, follow the header so it never ends
        // up covered; otherwise just re-clamp it into the (possibly new) range.
        if (currentTop >= prevMaxTop - 0.5) {
          currentTop = maxTop;
        } else {
          currentTop = clamp(currentTop, minTop, maxTop);
        }
      } else {
        // Shift the stick point opposite to the scroll and clamp to the range.
        currentTop = clamp(currentTop - deltaScroll, minTop, maxTop);
      }

      prevMaxTop = maxTop;
      stickyEl.style.position = 'sticky';
      stickyEl.style.top = currentTop + 'px';
    }

    function setup() {
      if (window.innerWidth < DESKTOP_BP) {
        disable();
        return;
      }

      var maxTop = getTopPad();
      var minTop = getBottomStick();

      enabled = true;

      if (minTop >= maxTop) {
        // Short card: simple native sticky below the header.
        wrapper.style.minHeight = '';
        currentTop = maxTop;
        prevMaxTop = maxTop;
        stickyEl.style.position = 'sticky';
        stickyEl.style.top = currentTop + 'px';
        lastScrollY = window.scrollY;
        return;
      }

      ensureMinHeight();

      // Seed currentTop from the card's current on-screen position so re-running
      // setup() (resize, variant change) doesn't jump the card.
      if (currentTop === null) {
        currentTop = maxTop;
      }
      currentTop = clamp(currentTop, minTop, maxTop);
      prevMaxTop = maxTop;
      stickyEl.style.position = 'sticky';
      stickyEl.style.top = currentTop + 'px';
      lastScrollY = window.scrollY;
    }

    function onScroll() {
      if (!enabled) return;
      var y = window.scrollY;
      var delta = y - lastScrollY;
      lastScrollY = y;
      applyTop(delta);
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setup, 250);
    });

    // Batch scroll handling to one execution per animation frame to avoid
    // multiple layout reads per frame.
    var scrollRAF = null;
    window.addEventListener('scroll', function () {
      if (!scrollRAF) {
        scrollRAF = requestAnimationFrame(function () {
          scrollRAF = null;
          onScroll();
        });
      }
    }, { passive: true });

    // The Globo secondary nav expands near the top of the page via a CSS
    // transition that can finish AFTER scrolling stops — so no scroll event
    // fires to reposition a card already pinned at the top, and the now-taller
    // header would overlap it. Watch the header's size and re-apply with a zero
    // scroll delta so a top-stuck card follows the header as it grows/shrinks.
    if (typeof ResizeObserver !== 'undefined' && headerEl) {
      var headerRO = new ResizeObserver(function () {
        if (enabled) applyTop(0);
      });
      headerRO.observe(headerEl);
    }

    setup();
  }

  function start() {
    setTimeout(init, 750);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
